import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, MapPin, Tag, BarChart3, FileText, Tv, Users } from 'lucide-react'
import { PublishedBroadcast } from '../../../services/broadcastService'
import { formatDuration, formatEventDate, formatChannelType, describeViewers } from './broadcastCustomerFormat'
import { useBroadcastOrigin } from './broadcastOrigin'

interface Props {
  broadcast: PublishedBroadcast
  /** Which of the broadcast views the caller is, so it can mark itself. */
 view: 'exposure' | 'brands' | 'details'
}

// Sponsor Exposure first: it answers "what was this broadcast worth", which is
// the question people arrive with. Unsponsored Brands is everything on screen
// nobody is paying for — the inventory behind the sold total. None of the three is the artefact a sponsor receives — that is the
// published report, generated separately and frozen at send time.
const TABS = [
  { id: 'exposure' as const, label: 'Sponsor Exposure', icon: FileText, path: '/exposure' },
  { id: 'brands' as const, label: 'Unsponsored Brands', icon: Tag, path: '' },
  { id: 'details' as const, label: 'Broadcast details', icon: BarChart3, path: '/details' }
]

/**
 * The identity of a broadcast, plus the sub-tabs between its views.
 *
 * The views are separate routes rather than state in one component so the
 * list's two actions can land directly on either, and so a link to a brand
 * breakdown stays a link to a brand breakdown. The tabs navigate; they do not
 * toggle local state.
 */
const BroadcastPageHeader: React.FC<Props> = ({ broadcast: b, view }) => {
  const navigate = useNavigate()
  const { origin, state } = useBroadcastOrigin()
  const date = formatEventDate(b.event_date)

  return (
    <div>
      <button onClick={() => navigate(origin?.path ?? '/broadcasts?tab=broadcasts')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" />{origin ? `Back to ${origin.label}` : 'All broadcasts'}
      </button>
      <h1 className="text-xl font-bold text-gray-900">{b.title}</h1>
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
        {date && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />{date}
          </span>
        )}
        {b.venue_name && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />{b.venue_name}
          </span>
        )}
        {b.league_short_name && <span>{b.league_short_name}</span>}
        {b.duration_ms && <span>{formatDuration(b.duration_ms)}</span>}
        {b.channel_name && (
          <span className="inline-flex items-center gap-1">
            <Tv className="h-3 w-3" />{b.channel_name}
            {b.channel_type && <span className="text-gray-400">· {formatChannelType(b.channel_type)}</span>}
          </span>
        )}
        {/* Audience with its source, on every tab: it is what the value is
            priced from, so a reader should never have to hunt for it. */}
        {b.viewers != null && (
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3" />{describeViewers(b)}
          </span>
        )}
      </div>

      <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner mt-4 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => navigate(`/broadcasts/${b.id}${tab.path}`, { state })}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                view === tab.id
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BroadcastPageHeader
